import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import Header from '../components/Header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Users, Plus, Search, Mail, Phone, Eye, Edit2, Trash2, 
  MapPin, Calendar, Building2, GraduationCap, RefreshCw,
  AlertTriangle, Send, User, Shield, FileText, X,
  Upload, Download, File, Folder, Clock
} from 'lucide-react';
import { toast } from 'sonner';

// French labels for FastAPI/Pydantic validation fields and messages, so the server's
// real reason for a failed student creation is shown clearly to the admin.
const FIELD_LABELS = {
  email: 'Email', first_name: 'Prénom', last_name: 'Nom', phone: 'Téléphone',
  date_of_birth: 'Date de naissance', gender: 'Genre', address: 'Adresse',
  postal_code: 'Code postal', city: 'Ville', country: 'Pays',
  course_id: 'Formation', company_id: 'Entreprise', opco_id: 'OPCO', group_id: 'Groupe',
  guardian_last_name: 'Nom du tuteur', guardian_first_name: 'Prénom du tuteur',
  guardian_email: 'Email du tuteur', guardian_phone: 'Téléphone du tuteur',
};
const MSG_LABELS = {
  'field required': 'ce champ est obligatoire',
  'Field required': 'ce champ est obligatoire',
  'value is not a valid email address': "l'adresse email n'est pas valide",
  'none is not an allowed value': 'ce champ est obligatoire',
};
const translateValidationItem = (item) => {
  if (typeof item === 'string') return item;
  const field = Array.isArray(item?.loc) ? item.loc[item.loc.length - 1] : '';
  const label = FIELD_LABELS[field] || field;
  const raw = item?.msg || '';
  const msg = MSG_LABELS[raw] || raw;
  return label ? `${label}: ${msg}` : msg;
};
// Turn any axios/FastAPI error into a readable French string (string, 422 array, or object detail).
const getApiErrorMessage = (error, fallback) => {
  if (!error?.response) return 'Connexion interrompue. Réessayez dans un instant.';
  const detail = error.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const parts = detail.map(translateValidationItem).filter(Boolean);
    return parts.length ? parts.join('\n') : fallback;
  }
  if (typeof detail === 'object') return translateValidationItem(detail) || fallback;
  return fallback;
};

// Strip characters that are not valid in a person/place name (blocks digits and
// special characters while keeping accents, spaces, hyphens and apostrophes).
const sanitizeName = (value) => {
  if (!value) return '';
  try {
    return value.replace(/[^\p{L}\s'-]/gu, '');
  } catch {
    // Fallback for engines without Unicode property escapes
    return value.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ\s'-]/g, '');
  }
};

// Country and nationality options (French) for the searchable datalist dropdowns.
const COUNTRIES = [
  'Afghanistan', 'Afrique du Sud', 'Albanie', 'Algérie', 'Allemagne', 'Andorre', 'Angola',
  'Arabie saoudite', 'Argentine', 'Arménie', 'Australie', 'Autriche', 'Azerbaïdjan', 'Bahamas',
  'Bahreïn', 'Bangladesh', 'Belgique', 'Bénin', 'Bhoutan', 'Biélorussie', 'Birmanie', 'Bolivie',
  'Bosnie-Herzégovine', 'Botswana', 'Brésil', 'Brunei', 'Bulgarie', 'Burkina Faso', 'Burundi',
  'Cambodge', 'Cameroun', 'Canada', 'Cap-Vert', 'Chili', 'Chine', 'Chypre', 'Colombie', 'Comores',
  'Congo', 'Congo (RDC)', 'Corée du Nord', 'Corée du Sud', 'Costa Rica', "Côte d'Ivoire", 'Croatie',
  'Cuba', 'Danemark', 'Djibouti', 'Dominique', 'Égypte', 'Émirats arabes unis', 'Équateur',
  'Érythrée', 'Espagne', 'Estonie', 'Eswatini', 'États-Unis', 'Éthiopie', 'Fidji', 'Finlande',
  'France', 'Gabon', 'Gambie', 'Géorgie', 'Ghana', 'Grèce', 'Grenade', 'Guatemala', 'Guinée',
  'Guinée-Bissau', 'Guinée équatoriale', 'Guyana', 'Haïti', 'Honduras', 'Hongrie', 'Îles Marshall',
  'Îles Salomon', 'Inde', 'Indonésie', 'Irak', 'Iran', 'Irlande', 'Islande', 'Israël', 'Italie',
  'Jamaïque', 'Japon', 'Jordanie', 'Kazakhstan', 'Kenya', 'Kirghizistan', 'Kiribati', 'Kosovo',
  'Koweït', 'Laos', 'Lesotho', 'Lettonie', 'Liban', 'Liberia', 'Libye', 'Liechtenstein', 'Lituanie',
  'Luxembourg', 'Macédoine du Nord', 'Madagascar', 'Malaisie', 'Malawi', 'Maldives', 'Mali', 'Malte',
  'Maroc', 'Maurice', 'Mauritanie', 'Mexique', 'Micronésie', 'Moldavie', 'Monaco', 'Mongolie',
  'Monténégro', 'Mozambique', 'Namibie', 'Nauru', 'Népal', 'Nicaragua', 'Niger', 'Nigeria', 'Norvège',
  'Nouvelle-Zélande', 'Oman', 'Ouganda', 'Ouzbékistan', 'Pakistan', 'Palaos', 'Palestine', 'Panama',
  'Papouasie-Nouvelle-Guinée', 'Paraguay', 'Pays-Bas', 'Pérou', 'Philippines', 'Pologne', 'Portugal',
  'Qatar', 'République centrafricaine', 'République dominicaine', 'République tchèque', 'Roumanie',
  'Royaume-Uni', 'Russie', 'Rwanda', 'Saint-Marin', 'Sainte-Lucie', 'Salvador', 'Samoa',
  'Sao Tomé-et-Principe', 'Sénégal', 'Serbie', 'Seychelles', 'Sierra Leone', 'Singapour', 'Slovaquie',
  'Slovénie', 'Somalie', 'Soudan', 'Soudan du Sud', 'Sri Lanka', 'Suède', 'Suisse', 'Suriname',
  'Syrie', 'Tadjikistan', 'Tanzanie', 'Tchad', 'Thaïlande', 'Timor oriental', 'Togo', 'Tonga',
  'Trinité-et-Tobago', 'Tunisie', 'Turkménistan', 'Turquie', 'Tuvalu', 'Ukraine', 'Uruguay',
  'Vanuatu', 'Vatican', 'Venezuela', 'Viêt Nam', 'Yémen', 'Zambie', 'Zimbabwe',
];
const NATIONALITIES = [
  'Afghane', 'Albanaise', 'Algérienne', 'Allemande', 'Américaine', 'Andorrane', 'Angolaise',
  'Argentine', 'Arménienne', 'Australienne', 'Autrichienne', 'Azerbaïdjanaise', 'Bahreïnienne',
  'Bangladaise', 'Belge', 'Béninoise', 'Bhoutanaise', 'Biélorusse', 'Birmane', 'Bolivienne',
  'Bosnienne', 'Botswanaise', 'Brésilienne', 'Britannique', 'Brunéienne', 'Bulgare', 'Burkinabée',
  'Burundaise', 'Cambodgienne', 'Camerounaise', 'Canadienne', 'Cap-verdienne', 'Centrafricaine',
  'Chilienne', 'Chinoise', 'Chypriote', 'Colombienne', 'Comorienne', 'Congolaise', 'Coréenne',
  'Costaricaine', 'Croate', 'Cubaine', 'Danoise', 'Djiboutienne', 'Dominicaine', 'Égyptienne',
  'Émirienne', 'Équatorienne', 'Érythréenne', 'Espagnole', 'Estonienne', 'Éthiopienne', 'Fidjienne',
  'Finlandaise', 'Française', 'Gabonaise', 'Gambienne', 'Géorgienne', 'Ghanéenne', 'Grecque',
  'Guatémaltèque', 'Guinéenne', 'Guyanienne', 'Haïtienne', 'Hondurienne', 'Hongroise', 'Indienne',
  'Indonésienne', 'Irakienne', 'Iranienne', 'Irlandaise', 'Islandaise', 'Israélienne', 'Italienne',
  'Ivoirienne', 'Jamaïcaine', 'Japonaise', 'Jordanienne', 'Kazakhe', 'Kényane', 'Kirghize', 'Kosovare',
  'Koweïtienne', 'Laotienne', 'Lesothane', 'Lettone', 'Libanaise', 'Libérienne', 'Libyenne',
  'Liechtensteinoise', 'Lituanienne', 'Luxembourgeoise', 'Macédonienne', 'Malgache', 'Malaisienne',
  'Malawite', 'Maldivienne', 'Malienne', 'Maltaise', 'Marocaine', 'Mauricienne', 'Mauritanienne',
  'Mexicaine', 'Moldave', 'Monégasque', 'Mongole', 'Monténégrine', 'Mozambicaine', 'Namibienne',
  'Népalaise', 'Nicaraguayenne', 'Nigériane', 'Nigérienne', 'Norvégienne', 'Néo-zélandaise',
  'Omanaise', 'Ougandaise', 'Ouzbèke', 'Pakistanaise', 'Palestinienne', 'Panaméenne', 'Paraguayenne',
  'Néerlandaise', 'Péruvienne', 'Philippine', 'Polonaise', 'Portugaise', 'Qatarienne', 'Roumaine',
  'Russe', 'Rwandaise', 'Salvadorienne', 'Samoane', 'Sénégalaise', 'Serbe', 'Seychelloise',
  'Sierra-léonaise', 'Singapourienne', 'Slovaque', 'Slovène', 'Somalienne', 'Soudanaise',
  'Sri-lankaise', 'Suédoise', 'Suisse', 'Surinamaise', 'Syrienne', 'Tadjike', 'Tanzanienne',
  'Tchadienne', 'Tchèque', 'Thaïlandaise', 'Togolaise', 'Tunisienne', 'Turkmène', 'Turque',
  'Ukrainienne', 'Uruguayenne', 'Vénézuélienne', 'Vietnamienne', 'Yéménite', 'Zambienne',
  'Zimbabwéenne',
];

// Controlled select so options stay in sync when reference data / filters change
const SimpleSelect = ({ value, onChange, options = [], placeholder, disabled, name }) => {
  const handleChange = (e) => {
    onChange(e.target.value);
  };

  return (
    <select
      value={value ?? ''}
      onChange={handleChange}
      disabled={disabled}
      name={name}
      style={{
        display: 'block',
        width: '100%',
        height: '40px',
        padding: '8px 12px',
        fontSize: '14px',
        lineHeight: '1.5',
        color: '#1e293b',
        backgroundColor: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '6px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <option value="" disabled>{placeholder || 'Sélectionner'}</option>
      {Array.isArray(options) && options.map((opt) => (
        <option key={String(opt.value)} value={String(opt.value)}>
          {opt.label}
        </option>
      ))}
    </select>
  );
};

const StudentsPage = ({ onMenuClick }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingReferenceData, setLoadingReferenceData] = useState(false);
  
  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Documents state
  const [detailTab, setDetailTab] = useState('info'); // 'info' or 'documents'
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [documentType, setDocumentType] = useState('');
  const [documentNotes, setDocumentNotes] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Reference data
  const [courses, setCourses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [opcos, setOpcos] = useState([]);
  
  // Form data with all fields
  const initialFormData = {
    // Identity (obligatoires)
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    gender: '',
    nationality: '',
    birth_place: '',
    birth_country: '',
    
    // Address (obligatoires)
    address: '',
    postal_code: '',
    city: '',
    country: '',
    
    // Training
    course_id: '',
    training_start_date: '',
    training_end_date: '',
    training_mode: '',
    professional_status: '',
    funding_type: '',
    company_id: '',
    opco_id: '',
    group_id: '',
    
    // Guardian (for minors)
    guardian_last_name: '',
    guardian_first_name: '',
    guardian_relationship: '',
    guardian_phone: '',
    guardian_email: '',
    guardian_address: '',
    
    // Emergency contact
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_email: '',
    
    // Admin
    admin_notes: ''
  };
  
  const [formData, setFormData] = useState(initialFormData);

  // Today's date (YYYY-MM-DD) used to cap the date of birth
  const todayStr = new Date().toISOString().split('T')[0];

  // Update a date field, rejecting years that are not exactly 4 digits
  // (a 5+ digit year crashes the backend on submit)
  const handleDateChange = (field, value) => {
    if (value) {
      const yearPart = value.split('-')[0];
      if (yearPart.length > 4) return; // ignore invalid input
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Update a phone field, keeping digits only and capping at 10
  const handlePhoneChange = (field, value) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    setFormData(prev => ({ ...prev, [field]: digits }));
  };

  // Calculate if person is minor based on date of birth
  const isMinor = useMemo(() => {
    if (!formData.date_of_birth) return false;
    const birthDate = new Date(formData.date_of_birth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age < 18;
  }, [formData.date_of_birth]);

  // Calculate age for display
  const calculatedAge = useMemo(() => {
    if (!formData.date_of_birth) return null;
    const birthDate = new Date(formData.date_of_birth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }, [formData.date_of_birth]);

  const fetchStudents = useCallback(async () => {
    try {
      const endpoint = user?.role === 'trainer' ? '/trainer/students' : '/students';
      const response = await api.get(endpoint);
      setStudents(response.data);
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error('Erreur lors du chargement des étudiants');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchReferenceData = useCallback(async () => {
    setLoadingReferenceData(true);
    try {
      // Load independently so one failure does not empty the whole form.
      // Prefer /content/courses, fall back to /courses (same pattern as live sessions).
      const [coursesRes, groupsRes] = await Promise.all([
        api.get('/content/courses').catch(() => api.get('/courses')).catch(() => ({ data: [] })),
        api.get('/groups').catch((err) => {
          console.error('Error fetching groups:', err);
          return { data: [] };
        }),
      ]);
      const coursesData = Array.isArray(coursesRes.data)
        ? coursesRes.data
        : Array.isArray(coursesRes.data?.items)
          ? coursesRes.data.items
          : [];
      const groupsData = Array.isArray(groupsRes.data)
        ? groupsRes.data
        : Array.isArray(groupsRes.data?.items)
          ? groupsRes.data.items
          : [];
      setCourses(coursesData);
      setGroups(groupsData);

      // Companies: FK students.company_id → companies.id (not finance beneficiaries).
      try {
        let companyList = [];
        try {
          const companiesRes = await api.get('/companies/dropdown');
          companyList = Array.isArray(companiesRes.data) ? companiesRes.data : [];
        } catch (e) {
          console.error('Companies dropdown not available', e);
        }
        // Fallback to full list (same source as /admin/companies)
        if (companyList.length === 0) {
          const listRes = await api.get('/companies', { params: { limit: 100 } }).catch(() => null);
          companyList = Array.isArray(listRes?.data?.companies) ? listRes.data.companies : [];
        }
        setCompanies(companyList);
      } catch (e) {
        console.error('Error loading companies', e);
        setCompanies([]);
      }

      // OPCOs: finance beneficiaries only (FK students.opco_id → beneficiaries.id).
      // Catalog is a last-resort fallback; backend maps catalog id → beneficiary on save.
      try {
        let opcoList = [];
        const isOpcoType = (b) =>
          String(b.beneficiary_type || '').toLowerCase().includes('opco');
        try {
          const beneficiariesRes = await api.get('/finance/beneficiaries', {
            params: { beneficiary_type: 'opco', is_active: true },
          });
          const rows = Array.isArray(beneficiariesRes.data) ? beneficiariesRes.data : [];
          opcoList = rows.filter(isOpcoType);
          if (opcoList.length === 0) {
            const allRes = await api.get('/finance/beneficiaries').catch(() => ({ data: [] }));
            opcoList = (Array.isArray(allRes.data) ? allRes.data : []).filter(isOpcoType);
          }
        } catch (e) {
          console.error('Beneficiaries OPCO list not available', e);
        }
        if (opcoList.length === 0) {
          try {
            const catalogRes = await api.get('/companies/opcos/list');
            opcoList = Array.isArray(catalogRes.data) ? catalogRes.data : [];
          } catch (e) {
            console.error('OPCO catalog not available', e);
          }
        }
        setOpcos(opcoList);
      } catch (e) {
        console.error('Error loading OPCOs', e);
        setOpcos([]);
      }
    } catch (error) {
      console.error('Error fetching reference data:', error);
    } finally {
      setLoadingReferenceData(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchStudents();
      if (user.role === 'cfa_admin' || user.role === 'super_admin') {
        fetchReferenceData();
      }
    }
  }, [user, fetchStudents, fetchReferenceData]);

  // When a formation is chosen, ensure groups linked to that course are present
  // (covers cases where the main /groups list was incomplete or filtered oddly).
  useEffect(() => {
    if (!formData.course_id) return;
    if (!showCreateModal && !showEditModal) return;
    if (user?.role !== 'cfa_admin' && user?.role !== 'super_admin') return;

    let cancelled = false;
    (async () => {
      try {
        const [byCourseRes, linkedRes] = await Promise.all([
          api.get('/groups', { params: { course_id: formData.course_id } }).catch(() => ({ data: [] })),
          api.get(`/enrollments/groups/course/${formData.course_id}`).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;

        const extra = [
          ...(Array.isArray(byCourseRes.data) ? byCourseRes.data : []),
          ...(Array.isArray(linkedRes.data) ? linkedRes.data : []),
        ];
        if (extra.length === 0) return;

        setGroups((prev) => {
          const byId = new Map(prev.map((g) => [g.id, g]));
          extra.forEach((g) => {
            if (g?.id == null) return;
            const existing = byId.get(g.id);
            byId.set(g.id, {
              ...existing,
              ...g,
              course_id: g.course_id ?? existing?.course_id ?? Number(formData.course_id),
              course_name: g.course_name ?? existing?.course_name,
              name: g.name || existing?.name || `Groupe ${g.id}`,
            });
          });
          return Array.from(byId.values());
        });
      } catch (e) {
        console.error('Error fetching groups for course:', e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [formData.course_id, showCreateModal, showEditModal, user?.role]);

  // Auto-open create modal if ?action=create in URL
  useEffect(() => {
    if (searchParams.get('action') === 'create' && user?.role === 'cfa_admin') {
      handleOpenCreate();
      // Replace (not push) so the Back button returns to the dashboard, not this modal.
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, user]);

  const resetForm = () => {
    setFormData(initialFormData);
  };

  const handleOpenCreate = () => {
    resetForm();
    setShowCreateModal(true);
    // Refresh formations/groupes when opening the modal (e.g. after creating a course)
    if (user?.role === 'cfa_admin' || user?.role === 'super_admin') {
      fetchReferenceData();
    }
  };

  const handleCloseCreate = () => {
    setShowCreateModal(false);
    resetForm();
  };

  const handleOpenEdit = (student) => {
    setSelectedStudent(student);
    setFormData({
      first_name: student.first_name || '',
      last_name: student.last_name || '',
      email: student.email || '',
      phone: student.phone || '',
      date_of_birth: student.date_of_birth || '',
      gender: student.gender || '',
      nationality: student.nationality || 'Française',
      birth_place: student.birth_place || '',
      birth_country: student.birth_country || 'France',
      address: student.address || '',
      postal_code: student.postal_code || '',
      city: student.city || '',
      country: student.country || 'France',
      course_id: student.course_id?.toString() || '',
      training_start_date: student.training_start_date || '',
      training_end_date: student.training_end_date || '',
      training_mode: student.training_mode || '',
      professional_status: student.professional_status || '',
      funding_type: student.funding_type || '',
      company_id: student.company_id?.toString() || '',
      opco_id: student.opco_id?.toString() || '',
      group_id: student.group_id?.toString() || '',
      guardian_last_name: student.guardian_last_name || '',
      guardian_first_name: student.guardian_first_name || '',
      guardian_relationship: student.guardian_relationship || '',
      guardian_phone: student.guardian_phone || '',
      guardian_email: student.guardian_email || '',
      guardian_address: student.guardian_address || '',
      emergency_contact_name: student.emergency_contact_name || '',
      emergency_contact_phone: student.emergency_contact_phone || '',
      emergency_contact_email: student.emergency_contact_email || '',
      admin_notes: student.admin_notes || ''
    });
    setShowEditModal(true);
    if (user?.role === 'cfa_admin' || user?.role === 'super_admin') {
      fetchReferenceData();
    }
  };

  const handleCloseEdit = () => {
    setShowEditModal(false);
    setSelectedStudent(null);
    resetForm();
  };

  const handleOpenDetail = (student) => {
    setSelectedStudent(student);
    setDetailTab('info');
    setDocuments([]);
    setShowDetailModal(true);
  };

  const handleCloseDetail = () => {
    setShowDetailModal(false);
    setSelectedStudent(null);
    setDetailTab('info');
    setDocuments([]);
    setDocumentType('');
    setDocumentNotes('');
    setSelectedFile(null);
  };

  // Document functions
  const fetchStudentDocuments = async (studentId) => {
    setLoadingDocuments(true);
    try {
      const response = await api.get(`/students/${studentId}/documents`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Erreur lors du chargement des documents');
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleUploadDocument = async () => {
    if (!documentType) {
      toast.error('Veuillez sélectionner un type de document');
      return;
    }
    if (!selectedFile) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }

    setUploadingDocument(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('document_type', documentType);
      formDataUpload.append('file', selectedFile);
      if (documentNotes) {
        formDataUpload.append('notes', documentNotes);
      }

      await api.post(`/students/${selectedStudent.id}/documents`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Document uploadé avec succès');
      setDocumentType('');
      setDocumentNotes('');
      setSelectedFile(null);
      fetchStudentDocuments(selectedStudent.id);
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Erreur lors de l\'upload du document');
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleDownloadDocument = async (documentId, filename) => {
    try {
      const response = await api.get(`/students/${selectedStudent.id}/documents/${documentId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) {
      return;
    }

    try {
      await api.delete(`/students/${selectedStudent.id}/documents/${documentId}`);
      toast.success('Document supprimé');
      fetchStudentDocuments(selectedStudent.id);
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Load documents when switching to documents tab
  useEffect(() => {
    if (detailTab === 'documents' && selectedStudent) {
      fetchStudentDocuments(selectedStudent.id);
    }
  }, [detailTab, selectedStudent]);

  // Validation
  const validateForm = () => {
    const errors = [];
    
    // Required fields
    if (!formData.last_name) errors.push('Nom');
    if (!formData.first_name) errors.push('Prénom');
    if (!formData.email) errors.push('Email');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) errors.push('Email (format invalide, ex. nom@domaine.com)');
    if (!formData.phone) errors.push('Téléphone');
    else if (formData.phone.replace(/\D/g, '').length !== 10) errors.push('Téléphone (10 chiffres)');
    if (!formData.date_of_birth) errors.push('Date de naissance');
    if (!formData.gender) errors.push('Sexe');
    if (!formData.address) errors.push('Adresse');
    if (!formData.postal_code) errors.push('Code postal');
    if (!formData.city) errors.push('Ville');
    
    // Formation — only required when at least one formation exists in the organization,
    // otherwise an empty dropdown would block every student creation.
    if (courses.length > 0 && !formData.course_id) errors.push('Formation suivie');

    // Training period coherence: end must not be before start
    if (formData.training_start_date && formData.training_end_date &&
        formData.training_end_date < formData.training_start_date) {
      toast.error('La date de fin doit être postérieure ou égale à la date de début.');
      return false;
    }

    // Guardian + emergency contact required if minor
    if (isMinor) {
      if (!formData.guardian_last_name) errors.push('Nom du tuteur');
      if (!formData.guardian_first_name) errors.push('Prénom du tuteur');
      if (!formData.guardian_phone) errors.push('Téléphone du tuteur');
      if (!formData.guardian_relationship) errors.push('Lien avec l\'étudiant');
      if (!formData.emergency_contact_name) errors.push('Contact d\'urgence (nom)');
      if (!formData.emergency_contact_phone) errors.push('Contact d\'urgence (téléphone)');
      else if (formData.emergency_contact_phone.replace(/\D/g, '').length !== 10) {
        errors.push('Contact d\'urgence (téléphone, 10 chiffres)');
      }
    }
    
    if (errors.length > 0) {
      toast.error(`Champs obligatoires manquants : ${errors.join(', ')}`);
      return false;
    }
    
    return true;
  };

  const handleCreateStudent = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const emptyToNull = (v) => (v === '' || v === undefined ? null : v);
      const payload = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim(),
        phone: formData.phone,
        date_of_birth: formData.date_of_birth,
        gender: formData.gender,
        nationality: emptyToNull(formData.nationality),
        birth_place: emptyToNull(formData.birth_place),
        birth_country: emptyToNull(formData.birth_country) || 'France',
        address: formData.address.trim(),
        postal_code: formData.postal_code.trim(),
        city: formData.city.trim(),
        country: emptyToNull(formData.country) || 'France',
        training_start_date: emptyToNull(formData.training_start_date),
        training_end_date: emptyToNull(formData.training_end_date),
        training_mode: emptyToNull(formData.training_mode),
        professional_status: emptyToNull(formData.professional_status),
        funding_type: emptyToNull(formData.funding_type),
        course_id: formData.course_id ? parseInt(formData.course_id, 10) : null,
        company_id: formData.company_id ? parseInt(formData.company_id, 10) : null,
        opco_id: formData.opco_id ? parseInt(formData.opco_id, 10) : null,
        group_id: formData.group_id ? parseInt(formData.group_id, 10) : null,
        guardian_last_name: emptyToNull(formData.guardian_last_name),
        guardian_first_name: emptyToNull(formData.guardian_first_name),
        guardian_relationship: emptyToNull(formData.guardian_relationship),
        guardian_phone: emptyToNull(formData.guardian_phone),
        guardian_email: emptyToNull(formData.guardian_email),
        guardian_address: emptyToNull(formData.guardian_address),
        emergency_contact_name: emptyToNull(formData.emergency_contact_name),
        emergency_contact_phone: emptyToNull(formData.emergency_contact_phone),
        emergency_contact_email: emptyToNull(formData.emergency_contact_email),
        admin_notes: emptyToNull(formData.admin_notes),
        is_minor: isMinor
      };

      await api.post('/students', payload);
      
      // Close modal first to prevent UI issues
      setShowCreateModal(false);
      resetForm();
      
      // Show success message
      toast.success('Étudiant créé avec succès. Un email d\'activation a été envoyé.');
      
      // Refresh list after a small delay to ensure modal is closed
      setTimeout(() => {
        fetchStudents();
      }, 100);
      
    } catch (error) {
      console.error('Error creating student:', error);
      toast.error(getApiErrorMessage(error, 'Erreur lors de la création de l\'étudiant'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStudent = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const emptyToNull = (v) => (v === '' || v === undefined ? null : v);
      const payload = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim(),
        phone: formData.phone,
        date_of_birth: formData.date_of_birth,
        gender: formData.gender,
        nationality: emptyToNull(formData.nationality),
        birth_place: emptyToNull(formData.birth_place),
        birth_country: emptyToNull(formData.birth_country) || 'France',
        address: formData.address.trim(),
        postal_code: formData.postal_code.trim(),
        city: formData.city.trim(),
        country: emptyToNull(formData.country) || 'France',
        training_start_date: emptyToNull(formData.training_start_date),
        training_end_date: emptyToNull(formData.training_end_date),
        training_mode: emptyToNull(formData.training_mode),
        professional_status: emptyToNull(formData.professional_status),
        funding_type: emptyToNull(formData.funding_type),
        course_id: formData.course_id ? parseInt(formData.course_id, 10) : null,
        company_id: formData.company_id ? parseInt(formData.company_id, 10) : null,
        opco_id: formData.opco_id ? parseInt(formData.opco_id, 10) : null,
        group_id: formData.group_id ? parseInt(formData.group_id, 10) : null,
        guardian_last_name: emptyToNull(formData.guardian_last_name),
        guardian_first_name: emptyToNull(formData.guardian_first_name),
        guardian_relationship: emptyToNull(formData.guardian_relationship),
        guardian_phone: emptyToNull(formData.guardian_phone),
        guardian_email: emptyToNull(formData.guardian_email),
        guardian_address: emptyToNull(formData.guardian_address),
        emergency_contact_name: emptyToNull(formData.emergency_contact_name),
        emergency_contact_phone: emptyToNull(formData.emergency_contact_phone),
        emergency_contact_email: emptyToNull(formData.emergency_contact_email),
        admin_notes: emptyToNull(formData.admin_notes),
        is_minor: isMinor
      };

      await api.put(`/students/${selectedStudent.id}`, payload);
      toast.success('Étudiant mis à jour avec succès');
      handleCloseEdit();
      fetchStudents();
    } catch (error) {
      console.error('Error updating student:', error);
      toast.error(getApiErrorMessage(error, 'Erreur lors de la mise à jour'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStudent = async (studentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet étudiant ? Cette action est irréversible.')) {
      return;
    }

    try {
      await api.delete(`/students/${studentId}`);
      toast.success('Étudiant supprimé');
      fetchStudents();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleResendActivation = async (studentId) => {
    try {
      await api.post(`/students/${studentId}/resend-activation`);
      toast.success('Email d\'activation renvoyé');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi de l\'email');
    }
  };

  const getStudentName = (student) => {
    return `${student.first_name || ''} ${student.last_name || ''}`.trim();
  };

  const getStudentInitials = (student) => {
    const first = student.first_name?.[0] || '';
    const last = student.last_name?.[0] || '';
    return `${first}${last}`.toUpperCase();
  };

  const getStatusBadge = (status, isActivated) => {
    if (status === 'pending_activation' || !isActivated) {
      return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">En attente</Badge>;
    }
    if (status === 'active') {
      return <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Actif</Badge>;
    }
    return <Badge variant="outline">{status}</Badge>;
  };

  const getFundingTypeBadge = (type) => {
    const types = {
      autofinancement: { label: 'Autofinancement', color: 'bg-blue-50 text-blue-700 border-blue-200' },
      opco: { label: 'OPCO', color: 'bg-violet-50 text-violet-700 border-violet-200' },
      entreprise: { label: 'Entreprise', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      mixte: { label: 'Mixte', color: 'bg-orange-50 text-orange-700 border-orange-200' }
    };
    const config = types[type] || { label: type || '-', color: 'bg-slate-50 text-slate-700 border-slate-200' };
    return <Badge variant="outline" className={config.color}>{config.label}</Badge>;
  };

  const filteredStudents = students.filter((student) => {
    const name = getStudentName(student).toLowerCase();
    const search = searchTerm.toLowerCase();
    return name.includes(search) || 
           student.email?.toLowerCase().includes(search) ||
           student.student_number?.toLowerCase().includes(search);
  });

  // Select options
  const genderOptions = [
    { value: 'M', label: 'Masculin' },
    { value: 'F', label: 'Féminin' },
    { value: 'Autre', label: 'Autre' }
  ];

  const trainingModeOptions = [
    { value: 'presentiel', label: 'Présentiel' },
    { value: 'distanciel', label: 'Distanciel' },
    { value: 'hybride', label: 'Hybride' }
  ];

  const professionalStatusOptions = [
    { value: 'etudiant', label: 'Étudiant' },
    { value: 'apprenti', label: 'Apprenti' },
    { value: 'salarie', label: 'Salarié' },
    { value: 'demandeur_emploi', label: 'Demandeur d\'emploi' },
    { value: 'independant', label: 'Indépendant' },
    { value: 'autre', label: 'Autre' }
  ];

  const fundingTypeOptions = [
    { value: 'autofinancement', label: 'Autofinancement' },
    { value: 'opco', label: 'OPCO' },
    { value: 'entreprise', label: 'Entreprise' },
    { value: 'mixte', label: 'Mixte' },
    { value: 'cpf', label: 'CPF' },
    { value: 'pole_emploi', label: 'Pôle Emploi' }
  ];

  const guardianRelationshipOptions = [
    { value: 'pere', label: 'Père' },
    { value: 'mere', label: 'Mère' },
    { value: 'tuteur_legal', label: 'Tuteur légal' },
    { value: 'autre', label: 'Autre' }
  ];

  const documentTypeOptions = [
    { value: 'piece_identite', label: "Pièce d'identité" },
    { value: 'justificatif_domicile', label: 'Justificatif de domicile' },
    { value: 'cv', label: 'CV' },
    { value: 'photo_identite', label: "Photo d'identité" },
    { value: 'rib', label: 'RIB' },
    { value: 'attestation_securite_sociale', label: 'Attestation sécurité sociale' },
    { value: 'titre_sejour', label: 'Titre de séjour' },
    { value: 'diplome', label: 'Diplôme' },
    { value: 'contrat_apprentissage', label: "Contrat d'apprentissage" },
    { value: 'convention', label: 'Convention' },
    { value: 'cerfa', label: 'CERFA' },
    { value: 'autre', label: 'Autre document' }
  ];

  const courseOptions = courses.map(c => ({ value: String(c.id), label: c.name || `Cours ${c.id}` }));
  // Prefer groups for the selected formation (or unassigned). If none match, still
  // show all groups so assigning a class is never blocked after picking a formation.
  const groupOptions = (() => {
    const toOption = (g) => ({
      value: String(g.id),
      label: g.course_name ? `${g.name} (${g.course_name})` : g.name,
    });
    if (!formData.course_id) {
      return groups.map(toOption);
    }
    const forCourse = groups.filter(
      (g) => g.course_id == null || String(g.course_id) === String(formData.course_id)
    );
    return (forCourse.length > 0 ? forCourse : groups).map(toOption);
  })();
  const companyOptions = (() => {
    const opts = companies.map(c => ({ value: String(c.id), label: c.name }));
    // Keep current company visible when editing inactive / missing from dropdown
    if (
      formData.company_id &&
      selectedStudent?.company_id &&
      String(selectedStudent.company_id) === String(formData.company_id) &&
      !opts.some((o) => o.value === String(formData.company_id))
    ) {
      opts.unshift({
        value: String(selectedStudent.company_id),
        label: selectedStudent.company_name || `Entreprise #${selectedStudent.company_id}`,
      });
    }
    return opts;
  })();
  const opcoOptions = (() => {
    const opts = opcos.map(o => ({ value: String(o.id), label: o.name }));
    if (
      formData.opco_id &&
      selectedStudent?.opco_id &&
      String(selectedStudent.opco_id) === String(formData.opco_id) &&
      !opts.some((o) => o.value === String(formData.opco_id))
    ) {
      opts.unshift({
        value: String(selectedStudent.opco_id),
        label: selectedStudent.opco_name || `OPCO #${selectedStudent.opco_id}`,
      });
    }
    return opts;
  })();
  const selectedCourse = courses.find((c) => String(c.id) === String(formData.course_id));
  const selectedGroup = groups.find((g) => String(g.id) === String(formData.group_id));
  const trainingModeLabel = (value) =>
    trainingModeOptions.find((o) => o.value === value)?.label || value;
  const professionalStatusLabel = (value) =>
    professionalStatusOptions.find((o) => o.value === value)?.label || value;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  // Render the student form
  const renderStudentForm = () => (
    <div className="space-y-6">
      {/* Searchable dropdown sources for country / nationality fields */}
      <datalist id="countries-list">
        {COUNTRIES.map((c) => <option key={c} value={c} />)}
      </datalist>
      <datalist id="nationalities-list">
        {NATIONALITIES.map((n) => <option key={n} value={n} />)}
      </datalist>

      {/* Section 1: Identité */}
      <div className="space-y-4">
        <h4 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          <User className="w-4 h-4 text-violet-600" />
          Identité <span className="text-xs text-slate-500">(obligatoire)</span>
        </h4>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Nom *</Label>
            <Input
              value={formData.last_name}
              onChange={(e) => setFormData({...formData, last_name: sanitizeName(e.target.value)})}
              placeholder="Dupont"
            />
          </div>
          <div>
            <Label>Prénom *</Label>
            <Input
              value={formData.first_name}
              onChange={(e) => setFormData({...formData, first_name: sanitizeName(e.target.value)})}
              placeholder="Jean"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Email *</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              placeholder="jean.dupont@email.com"
            />
          </div>
          <div>
            <Label>Téléphone *</Label>
            <Input
              type="tel"
              inputMode="numeric"
              value={formData.phone}
              onChange={(e) => handlePhoneChange('phone', e.target.value)}
              placeholder="0612345678"
              maxLength={10}
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label>Date de naissance *</Label>
            <Input
              type="date"
              min="1900-01-01"
              max={todayStr}
              value={formData.date_of_birth}
              onChange={(e) => handleDateChange('date_of_birth', e.target.value)}
            />
            {calculatedAge !== null && (
              <p className={`text-xs mt-1 ${isMinor ? 'text-amber-600 font-medium' : 'text-slate-500'}`}>
                {calculatedAge} ans {isMinor && '(Mineur)'}
              </p>
            )}
          </div>
          <div>
            <Label>Sexe *</Label>
            <SimpleSelect
              value={formData.gender}
              onChange={(val) => setFormData({...formData, gender: val})}
              options={genderOptions}
              placeholder="Sélectionner"
            />
          </div>
          <div>
            <Label>Nationalité</Label>
            <Input
              list="nationalities-list"
              value={formData.nationality}
              onChange={(e) => setFormData({...formData, nationality: e.target.value})}
              placeholder="Rechercher une nationalité..."
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Lieu de naissance</Label>
            <Input
              value={formData.birth_place}
              onChange={(e) => setFormData({...formData, birth_place: e.target.value})}
              placeholder="Paris"
            />
          </div>
          <div>
            <Label>Pays de naissance</Label>
            <Input
              list="countries-list"
              value={formData.birth_country}
              onChange={(e) => setFormData({...formData, birth_country: e.target.value})}
              placeholder="Rechercher un pays..."
            />
          </div>
        </div>
      </div>

      {/* Section 2: Adresse */}
      <div className="space-y-4 pt-4 border-t">
        <h4 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          <MapPin className="w-4 h-4 text-violet-600" />
          Adresse <span className="text-xs text-slate-500">(obligatoire)</span>
        </h4>
        
        <div>
          <Label>Adresse complète *</Label>
          <Textarea
            value={formData.address}
            onChange={(e) => setFormData({...formData, address: e.target.value})}
            placeholder="12 rue de la Formation"
            rows={2}
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label>Code postal *</Label>
            <Input
              value={formData.postal_code}
              onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
              placeholder="75001"
            />
          </div>
          <div>
            <Label>Ville *</Label>
            <Input
              value={formData.city}
              onChange={(e) => setFormData({...formData, city: e.target.value})}
              placeholder="Paris"
            />
          </div>
          <div>
            <Label>Pays</Label>
            <Input
              list="countries-list"
              value={formData.country}
              onChange={(e) => setFormData({...formData, country: e.target.value})}
              placeholder="Rechercher un pays..."
            />
          </div>
        </div>
      </div>

      {/* Section 3: Formation */}
      <div className="space-y-4 pt-4 border-t">
        <h4 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          <GraduationCap className="w-4 h-4 text-violet-600" />
          Formation <span className="text-xs text-slate-500">(obligatoire)</span>
        </h4>

        <p className="text-sm text-slate-600 dark:text-slate-400">
          Indiquez la formation suivie, le groupe éventuel, les dates et le mode de formation.
          Ces informations alimentent le suivi pédagogique, le planning et les documents administratifs.
        </p>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Formation suivie *</Label>
            <SimpleSelect
              value={formData.course_id}
              onChange={(val) => setFormData({
                ...formData,
                course_id: val,
                // Clear group if it no longer matches the selected formation
                group_id: (() => {
                  if (!val || !formData.group_id) return formData.group_id;
                  const currentGroup = groups.find((g) => String(g.id) === String(formData.group_id));
                  if (!currentGroup?.course_id) return formData.group_id;
                  return String(currentGroup.course_id) === String(val) ? formData.group_id : '';
                })(),
              })}
              options={courseOptions}
              placeholder={loadingReferenceData ? 'Chargement…' : 'Sélectionner une formation'}
              disabled={loadingReferenceData}
            />
            {!loadingReferenceData && courseOptions.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">
                Aucune formation disponible. Créez d'abord une formation dans « Gestion des Cours ».
              </p>
            )}
          </div>
          <div>
            <Label>Groupe / Classe</Label>
            <SimpleSelect
              value={formData.group_id}
              onChange={(val) => setFormData({...formData, group_id: val})}
              options={groupOptions}
              placeholder={loadingReferenceData ? 'Chargement…' : 'Sélectionner un groupe'}
              disabled={loadingReferenceData}
            />
          </div>
        </div>

        {selectedCourse && (
          <div className="rounded-lg border border-violet-200 bg-violet-50 dark:bg-violet-900/20 dark:border-violet-800 p-3 text-sm text-slate-700 dark:text-slate-200">
            <p className="font-medium text-violet-800 dark:text-violet-200 mb-1">
              {selectedCourse.name}
            </p>
            {selectedCourse.description && (
              <p className="text-slate-600 dark:text-slate-300 mb-2 line-clamp-2">
                {selectedCourse.description}
              </p>
            )}
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600 dark:text-slate-400">
              {(selectedCourse.duration_calculated?.formatted || selectedCourse.duration_hours) && (
                <span>
                  Durée :{' '}
                  {selectedCourse.duration_calculated?.total_minutes > 0
                    ? selectedCourse.duration_calculated.formatted
                    : `${selectedCourse.duration_hours}h`}
                </span>
              )}
              {selectedCourse.level && <span>Niveau : {selectedCourse.level}</span>}
              {selectedCourse.category && <span>Catégorie : {selectedCourse.category}</span>}
              {(selectedCourse.module_count != null || selectedCourse.lesson_count != null) && (
                <span>
                  Contenu : {selectedCourse.module_count || 0} module(s), {selectedCourse.lesson_count || 0} leçon(s)
                </span>
              )}
              {selectedCourse.trainer_name && <span>Formateur : {selectedCourse.trainer_name}</span>}
              {selectedGroup && <span>Groupe : {selectedGroup.name}</span>}
            </div>
          </div>
        )}

        {!loadingReferenceData && courses.length === 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 p-3 text-sm text-amber-800 dark:text-amber-200">
            <p className="font-medium mb-1">Aucune formation disponible</p>
            <p className="mb-2">
              Créez d&apos;abord une formation dans Gestion des Cours, puis éventuellement une classe dans Classes / Groupes.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="border-amber-300"
                onClick={() => navigate('/admin/courses')}
              >
                Créer une formation
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="border-amber-300"
                onClick={() => navigate('/admin/groups')}
              >
                Gérer les groupes
              </Button>
            </div>
          </div>
        )}

        {!loadingReferenceData && courses.length > 0 && groups.length === 0 && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 dark:bg-slate-800/50 dark:border-slate-700 p-3 text-sm text-slate-600 dark:text-slate-300">
            <p className="mb-2">
              Aucun groupe pour le moment. Vous pouvez créer l&apos;étudiant sans groupe, ou en ajouter un dans Classes / Groupes.
            </p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => navigate('/admin/groups')}
            >
              Créer un groupe
            </Button>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Date de début</Label>
            <Input
              type="date"
              min="1900-01-01"
              max="2100-12-31"
              value={formData.training_start_date}
              onChange={(e) => handleDateChange('training_start_date', e.target.value)}
            />
          </div>
          <div>
            <Label>Date de fin</Label>
            <Input
              type="date"
              min="1900-01-01"
              max="2100-12-31"
              value={formData.training_end_date}
              onChange={(e) => handleDateChange('training_end_date', e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Mode de formation</Label>
            <SimpleSelect
              value={formData.training_mode}
              onChange={(val) => setFormData({...formData, training_mode: val})}
              options={trainingModeOptions}
              placeholder="Sélectionner"
            />
          </div>
          <div>
            <Label>Situation professionnelle</Label>
            <SimpleSelect
              value={formData.professional_status}
              onChange={(val) => setFormData({...formData, professional_status: val})}
              options={professionalStatusOptions}
              placeholder="Sélectionner"
            />
          </div>
        </div>
      </div>

      {/* Section 4: Financement */}
      <div className="space-y-4 pt-4 border-t">
        <h4 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          <Building2 className="w-4 h-4 text-violet-600" />
          Financement <span className="text-xs text-slate-500">(optionnel)</span>
        </h4>
        
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label>Type de financement</Label>
            <SimpleSelect
              value={formData.funding_type}
              onChange={(val) => setFormData({...formData, funding_type: val})}
              options={fundingTypeOptions}
              placeholder="Sélectionner"
            />
          </div>
          <div>
            <Label>Entreprise</Label>
            <SimpleSelect
              value={formData.company_id}
              onChange={(val) => setFormData({...formData, company_id: val})}
              options={companyOptions}
              placeholder={loadingReferenceData ? 'Chargement…' : 'Sélectionner'}
              disabled={loadingReferenceData}
            />
          </div>
          <div>
            <Label>OPCO</Label>
            <SimpleSelect
              value={formData.opco_id}
              onChange={(val) => setFormData({...formData, opco_id: val})}
              options={opcoOptions}
              placeholder={loadingReferenceData ? 'Chargement…' : 'Sélectionner'}
              disabled={loadingReferenceData}
            />
          </div>
        </div>

        {!loadingReferenceData && companies.length === 0 && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 dark:bg-slate-800/50 dark:border-slate-700 p-3 text-sm text-slate-600 dark:text-slate-300">
            <p className="mb-2">
              Aucune entreprise enregistrée. Ajoutez-en une dans Entreprises pour pouvoir l&apos;associer à l&apos;étudiant.
            </p>
            <Button type="button" size="sm" variant="outline" onClick={() => navigate('/admin/companies')}>
              Gérer les entreprises
            </Button>
          </div>
        )}

        {!loadingReferenceData && opcos.length === 0 && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 dark:bg-slate-800/50 dark:border-slate-700 p-3 text-sm text-slate-600 dark:text-slate-300">
            <p className="mb-2">
              Aucun OPCO disponible. Vérifiez le catalogue OPCO (Entreprises) ou les bénéficiaires finance.
            </p>
            <Button type="button" size="sm" variant="outline" onClick={() => navigate('/admin/companies')}>
              Voir les entreprises / OPCO
            </Button>
          </div>
        )}
      </div>

      {/* Section 5: Tuteur / Représentant légal (conditional) */}
      {isMinor && (
        <div className="space-y-4 pt-4 border-t bg-amber-50 dark:bg-amber-900/20 -mx-4 px-4 py-4 rounded-lg">
          <h4 className="font-semibold text-amber-800 dark:text-amber-200 flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Tuteur / Représentant légal
            <Badge className="bg-amber-600 text-white text-xs">Obligatoire (mineur)</Badge>
          </h4>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Nom du tuteur *</Label>
              <Input
                value={formData.guardian_last_name}
                onChange={(e) => setFormData({...formData, guardian_last_name: sanitizeName(e.target.value)})}
                placeholder="Dupont"
                className="border-amber-300"
              />
            </div>
            <div>
              <Label>Prénom du tuteur *</Label>
              <Input
                value={formData.guardian_first_name}
                onChange={(e) => setFormData({...formData, guardian_first_name: sanitizeName(e.target.value)})}
                placeholder="Marie"
                className="border-amber-300"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Lien avec l'étudiant *</Label>
              <SimpleSelect
                value={formData.guardian_relationship}
                onChange={(val) => setFormData({...formData, guardian_relationship: val})}
                options={guardianRelationshipOptions}
                placeholder="Sélectionner"
              />
            </div>
            <div>
              <Label>Téléphone du tuteur *</Label>
              <Input
                inputMode="numeric"
                maxLength={10}
                value={formData.guardian_phone}
                onChange={(e) => handlePhoneChange('guardian_phone', e.target.value)}
                placeholder="0612345678"
                className="border-amber-300"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Email du tuteur</Label>
              <Input
                type="email"
                value={formData.guardian_email}
                onChange={(e) => setFormData({...formData, guardian_email: e.target.value})}
                placeholder="tuteur@email.com"
              />
            </div>
            <div>
              <Label>Adresse du tuteur (si différente)</Label>
              <Input
                value={formData.guardian_address}
                onChange={(e) => setFormData({...formData, guardian_address: e.target.value})}
                placeholder="Laisser vide si identique"
              />
            </div>
          </div>
        </div>
      )}

      {/* Section 6: Contact d'urgence */}
      <div className={`space-y-4 pt-4 border-t ${isMinor ? 'bg-amber-50 dark:bg-amber-900/20 -mx-4 px-4 py-4 rounded-lg' : ''}`}>
        <h4 className={`font-semibold flex items-center gap-2 ${isMinor ? 'text-amber-800 dark:text-amber-200' : 'text-slate-900 dark:text-white'}`}>
          <AlertTriangle className={`w-4 h-4 ${isMinor ? 'text-amber-600' : 'text-violet-600'}`} />
          Contact d&apos;urgence{' '}
          {isMinor ? (
            <Badge className="bg-amber-600 text-white text-xs">Obligatoire (mineur)</Badge>
          ) : (
            <span className="text-xs text-slate-500 font-normal">(recommandé)</span>
          )}
        </h4>

        {isMinor && (
          <p className="text-sm text-amber-700 dark:text-amber-300">
            Pour un étudiant de moins de 18 ans, le nom et le téléphone du contact d&apos;urgence sont obligatoires.
          </p>
        )}
        
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label>Nom complet{isMinor ? ' *' : ''}</Label>
            <Input
              value={formData.emergency_contact_name}
              onChange={(e) => setFormData({...formData, emergency_contact_name: e.target.value})}
              placeholder="Nom et prénom"
              className={isMinor ? 'border-amber-300' : undefined}
            />
          </div>
          <div>
            <Label>Téléphone{isMinor ? ' *' : ''}</Label>
            <Input
              inputMode="numeric"
              maxLength={10}
              value={formData.emergency_contact_phone}
              onChange={(e) => handlePhoneChange('emergency_contact_phone', e.target.value)}
              placeholder="0612345678"
              className={isMinor ? 'border-amber-300' : undefined}
            />
          </div>
          <div>
            <Label>Email</Label>
            <Input
              type="email"
              value={formData.emergency_contact_email}
              onChange={(e) => setFormData({...formData, emergency_contact_email: e.target.value})}
              placeholder="contact@email.com"
            />
          </div>
        </div>

        {isMinor && formData.guardian_first_name && formData.guardian_last_name && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="border-amber-300"
            onClick={() => setFormData((prev) => ({
              ...prev,
              emergency_contact_name: `${prev.guardian_first_name} ${prev.guardian_last_name}`.trim(),
              emergency_contact_phone: prev.guardian_phone || prev.emergency_contact_phone,
              emergency_contact_email: prev.guardian_email || prev.emergency_contact_email,
            }))}
          >
            Utiliser le tuteur comme contact d&apos;urgence
          </Button>
        )}
      </div>

      {/* Section 7: Notes administratives */}
      <div className="space-y-4 pt-4 border-t">
        <h4 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          <FileText className="w-4 h-4 text-violet-600" />
          Notes administratives <span className="text-xs text-slate-500">(interne)</span>
        </h4>
        
        <div>
          <Label>Commentaires / Notes internes</Label>
          <Textarea
            value={formData.admin_notes}
            onChange={(e) => setFormData({...formData, admin_notes: e.target.value})}
            placeholder="Informations complémentaires, remarques importantes..."
            rows={3}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Gestion des Étudiants" onMenuClick={onMenuClick} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-2">Liste des Étudiants</h2>
            <p className="text-slate-600 dark:text-slate-400">{students.length} étudiant(s) au total</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={fetchStudents} data-testid="refresh-students-btn" title="Actualiser">
              <RefreshCw className="w-4 h-4" />
            </Button>
            {(user?.role === 'cfa_admin' || user?.role === 'super_admin') && (
              <Button onClick={handleOpenCreate} data-testid="add-student-btn" className="bg-violet-600 hover:bg-violet-700">
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un étudiant
              </Button>
            )}
          </div>
        </div>

        {/* Search */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Rechercher un étudiant par nom, email ou numéro..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Students Grid */}
        {filteredStudents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredStudents.map((student) => (
              <Card key={student.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-semibold text-lg">
                          {getStudentInitials(student)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900 dark:text-white truncate">
                          {getStudentName(student)}
                        </h3>
                        <p className="text-xs text-slate-500">{student.student_number}</p>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {getStatusBadge(student.status, student.is_activated)}
                          {student.is_minor && (
                            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                              Mineur
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                      <Mail className="w-4 h-4" />
                      <span className="truncate">{student.email}</span>
                    </div>
                    {student.phone && (
                      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                        <Phone className="w-4 h-4" />
                        <span>{student.phone}</span>
                      </div>
                    )}
                    {student.course_name && (
                      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                        <GraduationCap className="w-4 h-4" />
                        <span className="truncate">{student.course_name}</span>
                      </div>
                    )}
                    {student.group_name && (
                      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                        <Users className="w-4 h-4" />
                        <span className="truncate">{student.group_name}</span>
                      </div>
                    )}
                    {student.funding_type && (
                      <div className="mt-2">
                        {getFundingTypeBadge(student.funding_type)}
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => handleOpenDetail(student)}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Voir
                    </Button>
                    {user?.role === 'cfa_admin' && (
                      <>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleOpenEdit(student)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDeleteStudent(student.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                {searchTerm ? 'Aucun résultat' : 'Aucun étudiant'}
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                {searchTerm 
                  ? 'Modifiez votre recherche pour trouver des étudiants' 
                  : 'Commencez par ajouter votre premier étudiant'}
              </p>
              {user?.role === 'cfa_admin' && !searchTerm && (
                <Button onClick={handleOpenCreate}>
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter un étudiant
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      {/* Create Modal - Using simple div instead of Dialog to avoid Radix issues */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 z-0" onClick={handleCloseCreate} />
          <div className="relative z-10 bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-3xl flex flex-col overflow-hidden" style={{ maxHeight: 'calc(100dvh - 2rem)' }}>
            {/* Header - Fixed */}
            <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
              <div>
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-violet-600" />
                  Nouvel étudiant
                </h2>
                <p className="text-sm text-slate-500 mt-1">Un email d'activation sera envoyé automatiquement</p>
              </div>
              <Button variant="ghost" size="sm" onClick={handleCloseCreate}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Content - Scrollable */}
            <div className="flex-1 min-h-0 overflow-y-auto p-4">
              {renderStudentForm()}
            </div>
            
            {/* Footer - Fixed */}
            <div className="flex-shrink-0 flex justify-end gap-3 p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
              <Button variant="outline" onClick={handleCloseCreate}>
                Annuler
              </Button>
              <Button onClick={handleCreateStudent} disabled={saving}>
                {saving ? (
                  <span className="flex items-center">
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Création...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <Plus className="w-4 h-4 mr-2" />
                    Créer l'étudiant
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedStudent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 z-0" onClick={handleCloseEdit} />
          <div className="relative z-10 bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-3xl flex flex-col overflow-hidden" style={{ maxHeight: 'calc(100dvh - 2rem)' }}>
            {/* Header - Fixed */}
            <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
              <div>
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                  <Edit2 className="w-5 h-5 text-violet-600" />
                  Modifier l'étudiant
                </h2>
                <p className="text-sm text-slate-500 mt-1">{selectedStudent.first_name} {selectedStudent.last_name}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={handleCloseEdit}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Content - Scrollable */}
            <div className="flex-1 min-h-0 overflow-y-auto p-4">
              {renderStudentForm()}
            </div>
            
            {/* Footer - Fixed */}
            <div className="flex-shrink-0 flex justify-end gap-3 p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
              <Button variant="outline" onClick={handleCloseEdit}>
                Annuler
              </Button>
              <Button onClick={handleUpdateStudent} disabled={saving}>
                {saving ? (
                  <span className="flex items-center">
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Enregistrement...
                  </span>
                ) : (
                  'Enregistrer'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedStudent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 z-0" onClick={handleCloseDetail} />
          <div className="relative z-10 bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                  <span className="text-white font-bold text-lg">
                    {getStudentInitials(selectedStudent)}
                  </span>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                    {getStudentName(selectedStudent)}
                  </h2>
                  <p className="text-sm text-slate-500">{selectedStudent.student_number}</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={handleCloseDetail}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Tabs - Using native buttons to avoid Radix conflicts */}
            <div className="flex-shrink-0 flex border-b border-slate-200 dark:border-slate-700 px-6">
              <button
                type="button"
                onClick={() => setDetailTab('info')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center ${
                  detailTab === 'info'
                    ? 'border-violet-600 text-violet-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <User className="w-4 h-4 mr-2" />
                Informations
              </button>
              <button
                type="button"
                onClick={() => setDetailTab('documents')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center ${
                  detailTab === 'documents'
                    ? 'border-violet-600 text-violet-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <Folder className="w-4 h-4 mr-2" />
                Documents
                {documents.length > 0 && (
                  <span className="ml-2 bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-300 text-xs px-1.5 py-0.5 rounded">{documents.length}</span>
                )}
              </button>
            </div>
            
            {/* Content - Using key to force re-render and avoid DOM conflicts */}
            <div className="flex-1 overflow-y-auto p-6" key={`detail-content-${detailTab}`}>
              {detailTab === 'info' && (
                <div className="space-y-6">
                  {/* Badges */}
                  <div className="flex flex-wrap gap-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      selectedStudent.status === 'pending_activation' || !selectedStudent.is_activated
                        ? 'bg-amber-100 text-amber-800 border border-amber-200'
                        : 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                    }`}>
                      {selectedStudent.status === 'pending_activation' || !selectedStudent.is_activated ? 'En attente' : 'Actif'}
                    </span>
                    {selectedStudent.funding_type && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                        {fundingTypeOptions.find(f => f.value === selectedStudent.funding_type)?.label || selectedStudent.funding_type}
                      </span>
                    )}
                    {selectedStudent.is_minor && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                        Mineur
                      </span>
                    )}
                  </div>

                  {/* Contact */}
                  <div>
                    <h4 className="font-medium text-slate-900 dark:text-white mb-2">Contact</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                        <Mail className="w-4 h-4" />
                        {selectedStudent.email}
                      </div>
                      {selectedStudent.phone && (
                        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                          <Phone className="w-4 h-4" />
                          {selectedStudent.phone}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Informations personnelles */}
                  <div>
                    <h4 className="font-medium text-slate-900 dark:text-white mb-2">Informations personnelles</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm text-slate-600 dark:text-slate-400">
                      {selectedStudent.date_of_birth && (
                        <div>Date de naissance : {selectedStudent.date_of_birth}</div>
                      )}
                      {selectedStudent.gender && (
                        <div>Sexe : {selectedStudent.gender === 'M' ? 'Masculin' : selectedStudent.gender === 'F' ? 'Féminin' : selectedStudent.gender}</div>
                      )}
                      {selectedStudent.nationality && (
                        <div>Nationalité : {selectedStudent.nationality}</div>
                      )}
                      {selectedStudent.birth_place && (
                        <div>Né(e) à : {selectedStudent.birth_place}{selectedStudent.birth_country ? `, ${selectedStudent.birth_country}` : ''}</div>
                      )}
                    </div>
                  </div>

                  {/* Adresse */}
                  {(selectedStudent.address || selectedStudent.city) && (
                    <div>
                      <h4 className="font-medium text-slate-900 dark:text-white mb-2">Adresse</h4>
                      <div className="text-sm text-slate-600 dark:text-slate-400">
                        {selectedStudent.address && <div>{selectedStudent.address}</div>}
                        <div>{selectedStudent.postal_code} {selectedStudent.city}{selectedStudent.country && selectedStudent.country !== 'France' ? `, ${selectedStudent.country}` : ''}</div>
                      </div>
                    </div>
                  )}

                  {/* Formation */}
                  {(selectedStudent.course_name || selectedStudent.training_start_date || selectedStudent.training_mode) && (
                    <div>
                      <h4 className="font-medium text-slate-900 dark:text-white mb-2">Formation</h4>
                      <div className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
                        {selectedStudent.course_name && <div>Formation : {selectedStudent.course_name}</div>}
                        {selectedStudent.group_name && <div>Groupe : {selectedStudent.group_name}</div>}
                        {selectedStudent.training_start_date && (
                          <div>Période : {selectedStudent.training_start_date} → {selectedStudent.training_end_date || '...'}</div>
                        )}
                        {selectedStudent.training_mode && (
                          <div>Mode : {trainingModeLabel(selectedStudent.training_mode)}</div>
                        )}
                        {selectedStudent.professional_status && (
                          <div>Situation : {professionalStatusLabel(selectedStudent.professional_status)}</div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Guardian (if minor) */}
                  {selectedStudent.is_minor && selectedStudent.guardian_last_name && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg">
                      <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-2 flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        Tuteur / Représentant légal
                      </h4>
                      <div className="text-sm text-amber-700 dark:text-amber-300 space-y-1">
                        <div>{selectedStudent.guardian_first_name} {selectedStudent.guardian_last_name}</div>
                        {selectedStudent.guardian_relationship && <div>Lien : {selectedStudent.guardian_relationship}</div>}
                        {selectedStudent.guardian_phone && <div>Tél : {selectedStudent.guardian_phone}</div>}
                        {selectedStudent.guardian_email && <div>Email : {selectedStudent.guardian_email}</div>}
                      </div>
                    </div>
                  )}

                  {/* Emergency contact */}
                  {(selectedStudent.emergency_contact_name || selectedStudent.emergency_contact_phone) && (
                    <div className={selectedStudent.is_minor ? 'bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg' : ''}>
                      <h4 className={`font-medium mb-2 flex items-center gap-2 ${selectedStudent.is_minor ? 'text-amber-800 dark:text-amber-200' : 'text-slate-900 dark:text-white'}`}>
                        <AlertTriangle className="w-4 h-4" />
                        Contact d&apos;urgence
                      </h4>
                      <div className={`text-sm space-y-1 ${selectedStudent.is_minor ? 'text-amber-700 dark:text-amber-300' : 'text-slate-600 dark:text-slate-400'}`}>
                        {selectedStudent.emergency_contact_name && <div>{selectedStudent.emergency_contact_name}</div>}
                        {selectedStudent.emergency_contact_phone && <div>Tél : {selectedStudent.emergency_contact_phone}</div>}
                        {selectedStudent.emergency_contact_email && <div>Email : {selectedStudent.emergency_contact_email}</div>}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  {(user?.role === 'cfa_admin' || user?.role === 'super_admin') && selectedStudent.status === 'pending_activation' && (
                    <div className="pt-4 border-t">
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={() => {
                          handleResendActivation(selectedStudent.id);
                          handleCloseDetail();
                        }}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Renvoyer l'email d'activation
                      </Button>
                    </div>
                  )}
                </div>
              )}
              {detailTab === 'documents' && (
                <div className="space-y-6">
                  {/* Upload Section */}
                  <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
                    <h4 className="font-medium text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                      <Upload className="w-4 h-4 text-violet-600" />
                      Ajouter un document
                    </h4>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <Label>Type de document *</Label>
                        <SimpleSelect
                          value={documentType}
                          onChange={setDocumentType}
                          options={documentTypeOptions}
                          placeholder="Sélectionner un type"
                        />
                      </div>
                      <div>
                        <Label>Fichier *</Label>
                        <Input
                          type="file"
                          onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                          className="cursor-pointer"
                        />
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <Label>Notes (optionnel)</Label>
                      <Input
                        value={documentNotes}
                        onChange={(e) => setDocumentNotes(e.target.value)}
                        placeholder="Commentaire sur ce document..."
                      />
                    </div>
                    
                    <Button 
                      onClick={handleUploadDocument} 
                      disabled={uploadingDocument || !documentType || !selectedFile}
                      className="w-full"
                    >
                      {uploadingDocument ? (
                        <span className="flex items-center">
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Upload en cours...
                        </span>
                      ) : (
                        <span className="flex items-center">
                          <Upload className="w-4 h-4 mr-2" />
                          Uploader le document
                        </span>
                      )}
                    </Button>
                  </div>

                  {/* Documents List */}
                  <div>
                    <h4 className="font-medium text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-violet-600" />
                      Documents du dossier ({documents.length})
                    </h4>
                    
                    {loadingDocuments ? (
                      <div className="text-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto text-violet-600" />
                        <p className="text-sm text-slate-500 mt-2">Chargement...</p>
                      </div>
                    ) : documents.length === 0 ? (
                      <div className="text-center py-8 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                        <Folder className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">Aucun document dans le dossier</p>
                        <p className="text-sm text-slate-400 mt-1">Utilisez le formulaire ci-dessus pour ajouter des documents</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {documents.map((doc) => (
                          <div key={doc.id} className="flex items-center justify-between p-4 bg-white dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center flex-shrink-0">
                                <File className="w-5 h-5 text-violet-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-800 border border-violet-200">
                                    {doc.document_type_label}
                                  </span>
                                </div>
                                <p className="font-medium text-slate-900 dark:text-white truncate mt-1">
                                  {doc.original_filename}
                                </p>
                                <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {new Date(doc.uploaded_at).toLocaleDateString('fr-FR')}
                                  </span>
                                  <span>par {doc.uploaded_by}</span>
                                  {doc.file_size && (
                                    <span>{(doc.file_size / 1024).toFixed(1)} Ko</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadDocument(doc.id, doc.original_filename)}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteDocument(doc.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentsPage;
